'use client';

import { useState, useEffect, useCallback, useMemo, createContext, useContext, ReactNode } from 'react';
import { Character, CharacterChatHistory, CharacterMetadata } from '@/app/lib/types/character';
import {
  globalChatState,
  CharacterLoadingState,
  ImageWithStatus,
  ChatStatus
} from '../data/globalChatState';
// import { PageParams } from '../types/chat.types';
import { LiveImageStatus } from '../data/liveMessageData/liveMessage';
import { Room } from 'livekit-client';
import { LocalUserChoices } from '@livekit/components-react';
import { ConnectionDetails } from '@/lib/types';
import { UserChatSetting, ImageGenMode } from '../data/chatSetting/settingOptions';

/**
 * Combined state interface for all global chat state
 */
export interface GlobalChatStateData {
  // Character data
  character: Character | null;
  
  // Character loading state
  isCharacterLoading: boolean;
  characterLoadingError: string | null;
  
  // image 
  activeImage: ImageWithStatus;
  profileImage: ImageWithStatus;
  
  // Chat status
  chatStatus: ChatStatus;
  debugPlayBack: boolean;
  debugAutoLaunchVideo: boolean;
  
  // Chat UI state
  isImageGenerating: boolean;
  streamingMessageId: string;
  lastProcessedIndexForTranscriptionTracking: number;
  
  // Inactivity timeout state
  isInactivityTimeoutPopupOpen: boolean;
  inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
  
  // Agent signal state
  agentAvatarEntered: boolean;
  hasAgentAvatar: boolean;
  
  // Hint state
  isHintLoading: boolean;
  hints: string[];
  showHintPanel: boolean;
  canGenerateHint: boolean;
  agentAsrEntered: boolean;
  hasAgentAsr: boolean;
  
  // User chat settings
  userChatSetting: UserChatSetting;
}

/**
 * Actions interface for modifying global chat state
 */
export interface GlobalChatStateActions {
  // Character actions
  loadCharacter: (avatarId: string, 
    cachedCharacterMetadata?: CharacterMetadata | null,
    cachedCharacterChatHistory?: CharacterChatHistory | null,
    preloadedPresignedUrl?: boolean,
    locale?: string | null) => Promise<void>;
  
  // Image actions
  setActiveImage: (s3Key: string) => Promise<void>; // accept s3 key
  
  // Chat status actions
  setChatStatus: (status: ChatStatus) => void;
  setDebugPlayBack: (debugPlayBack: boolean) => void;
  setDebugAutoLaunchVideo: (debugAutoLaunchVideo: boolean) => void;
  
  // Chat UI state actions
  setIsImageGenerating: (isGenerating: boolean) => void;
  setStreamingMessageId: (messageId: string) => void;
  setLastProcessedIndexForTranscriptionTracking: (index: number) => void;
  
  // Inactivity timeout actions
  setInactivityTimeoutState: (state: {
    isInactivityTimeoutPopupOpen: boolean;
    inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
  }) => void;
  clearInactivityTimeoutState: () => void;
  resetInactivityTimer: () => void;
  // clearInactivityTimer: () => void; // Internal use only - not exposed to components
  
  // Agent signal actions
  setAgentAvatarEntered: (entered: boolean) => void;
  setAgentAsrEntered: (entered: boolean) => void;
  resetAgentSignals: () => void;
  
  // Hint state actions
  setHintLoading: (isLoading: boolean) => void;
  setHints: (hints: string[]) => void;
  setShowHintPanel: (show: boolean) => void;
  setCanGenerateHint: (canGenerate: boolean) => void;
  resetHintState: () => void;
  
  // User chat settings actions
  setUserChatSetting: (settings: UserChatSetting) => void;
  setLanguageModel: (languageModel: string) => void;
  setImageGenMode: (imageGenMode: ImageGenMode) => void;
  setTtsId: (ttsId: string) => void;
  setImageModel: (imageModel: string) => void;

  // Utility actions
  reset: () => void;
  resetFrontendChatHistory: () => void;
}

/**
 * Context for GlobalChatState
 */
interface GlobalChatStateContextType {
  globalChatState: typeof globalChatState;
}

const GlobalChatStateContext = createContext<GlobalChatStateContextType | undefined>(undefined);

/**
 * Provider for GlobalChatState context
 */
export function GlobalChatStateProvider({ children }: { children: ReactNode }) {
  return (
    <GlobalChatStateContext.Provider value={{ globalChatState }}>
      {children}
    </GlobalChatStateContext.Provider>
  );
}

/**
 * Hook to access GlobalChatState context
 */
function useGlobalChatStateContext() {
  const context = useContext(GlobalChatStateContext);
  if (context === undefined) {
    throw new Error('useGlobalChatState must be used within a GlobalChatStateProvider');
  }
  return context;
}

/**
 * React hook for accessing and managing global chat state
 * Provides automatic re-rendering when state changes
 */
export function useGlobalChatState(): [GlobalChatStateData, GlobalChatStateActions] {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  // Local state that triggers re-renders (following LiveMessage pattern)
  const [, forceUpdate] = useState({});

  // Subscribe to state changes (following LiveMessage pattern exactly)
  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]); // Empty dependency array like LiveMessage hooks
  
  // Get current state data
  const stateData: GlobalChatStateData = {
    // Character data
    character: globalChatStateInstance.getCharacter(),

    // Character loading state
    ...globalChatStateInstance.getCharacterLoadingState(),

    // Images
    activeImage: globalChatStateInstance.getActiveImage(),
    profileImage: globalChatStateInstance.getProfileImage(),

    // Chat status
    chatStatus: globalChatStateInstance.getChatStatus(),
    debugPlayBack: globalChatStateInstance.getDebugPlayBack(),
    debugAutoLaunchVideo: globalChatStateInstance.getDebugAutoLaunchVideo(),

    // Chat UI state
    isImageGenerating: globalChatStateInstance.getIsImageGenerating(),
    streamingMessageId: globalChatStateInstance.getStreamingMessageId(),
    lastProcessedIndexForTranscriptionTracking: globalChatStateInstance.getLastProcessedIndexForTranscriptionTracking(),

    // Inactivity timeout state
    ...globalChatStateInstance.getInactivityTimeoutState(),

    // Agent signal state
    agentAvatarEntered: globalChatStateInstance.getAgentAvatarEntered(),
    hasAgentAvatar: globalChatStateInstance.hasAgentAvatar(),

    // Hint state
    isHintLoading: globalChatStateInstance.getHintLoading(),
    hints: globalChatStateInstance.getHints(),
    showHintPanel: globalChatStateInstance.getShowHintPanel(),
    canGenerateHint: globalChatStateInstance.getCanGenerateHint(),
    // stream start state
    agentAsrEntered: globalChatStateInstance.getAgentAsrEntered(),
    hasAgentAsr: globalChatStateInstance.hasAgentAsr(),

    // User chat settings
    userChatSetting: globalChatStateInstance.getUserChatSetting(),
  };
  
  // Actions for modifying state - MEMOIZED to prevent infinite loops
  const actions: GlobalChatStateActions = useMemo(() => ({
    // Character actions
    loadCharacter: async (avatarId: string,
       cachedCharacterMetadata: CharacterMetadata | null = null,
       cachedCharacterChatHistory: CharacterChatHistory | null = null,
       preloadedPresignedUrl: boolean = false,
       locale?: string | null) => {
      await globalChatStateInstance.loadCharacter(avatarId,
        cachedCharacterMetadata, cachedCharacterChatHistory, preloadedPresignedUrl, locale);
    },


    // Image actions
    setActiveImage: (s3Key: string) => {
      return globalChatStateInstance.setActiveImage(s3Key);
    },

    // Chat status actions
    setChatStatus: (status: ChatStatus) => {
      globalChatStateInstance.setChatStatus(status);
    },

    setDebugPlayBack: (debugPlayBack: boolean) => {
      globalChatStateInstance.setDebugPlayBack(debugPlayBack);
    },

    setDebugAutoLaunchVideo: (debugAutoLaunchVideo: boolean) => {
      globalChatStateInstance.setDebugAutoLaunchVideo(debugAutoLaunchVideo);
    },

    // Chat UI state actions
    setIsImageGenerating: (isGenerating: boolean) => {
      globalChatStateInstance.setIsImageGenerating(isGenerating);
    },

    setStreamingMessageId: (messageId: string) => {
      globalChatStateInstance.setStreamingMessageId(messageId);
    },

    setLastProcessedIndexForTranscriptionTracking: (index: number) => {
      globalChatStateInstance.setLastProcessedIndexForTranscriptionTracking(index);
    },

    // Inactivity timeout actions
    setInactivityTimeoutState: (state: {
      isInactivityTimeoutPopupOpen: boolean;
      inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
    }) => {
      globalChatStateInstance.setInactivityTimeoutState(state);
    },

    clearInactivityTimeoutState: () => {
      globalChatStateInstance.clearInactivityTimeoutState();
    },

    resetInactivityTimer: () => {
      globalChatStateInstance.resetInactivityTimer();
    },

    // clearInactivityTimer is internal only - not exposed to components

    // Agent signal actions
    setAgentAvatarEntered: (entered: boolean) => {
      globalChatStateInstance.setAgentAvatarEntered(entered);
    },

    setAgentAsrEntered: (entered: boolean) => {
      globalChatStateInstance.setAgentAsrEntered(entered);
    },

    resetAgentSignals: () => {
      globalChatStateInstance.resetAgentSignals();
    },

    // Hint state actions
    setHintLoading: (isLoading: boolean) => {
      globalChatStateInstance.setHintLoading(isLoading);
    },

    setHints: (hints: string[]) => {
      globalChatStateInstance.setHints(hints);
    },

    setShowHintPanel: (show: boolean) => {
      globalChatStateInstance.setShowHintPanel(show);
    },

    setCanGenerateHint: (canGenerate: boolean) => {
      globalChatStateInstance.setCanGenerateHint(canGenerate);
    },

    resetHintState: () => {
      globalChatStateInstance.resetHintState();
    },

    // User chat settings actions
    setUserChatSetting: (settings: UserChatSetting) => {
      globalChatStateInstance.setUserChatSetting(settings);
    },

    setLanguageModel: (languageModel: string) => {
      globalChatStateInstance.setLanguageModel(languageModel);
    },

    setImageGenMode: (imageGenMode: ImageGenMode) => {
      globalChatStateInstance.setImageGenMode(imageGenMode);
    },

    setTtsId: (ttsId: string) => {
      globalChatStateInstance.setTtsId(ttsId);
    },

    setImageModel: (imageModel: string) => {
      globalChatStateInstance.setImageModel(imageModel);
    },

    // Utility actions
    reset: () => {
      globalChatStateInstance.reset();
    },

    resetFrontendChatHistory: () => {
      globalChatStateInstance.resetFrontendChatHistory();
    },
  }), [globalChatStateInstance]); // Include globalChatStateInstance in dependency array
  
  return [stateData, actions];
}

/**
 * Hook for accessing only character data (lighter weight)
 */
export function useCharacterData() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  return {
    character: globalChatStateInstance.getCharacter(),
    ...globalChatStateInstance.getCharacterLoadingState(),
  };
}

/**
 * Hook for accessing only active image data (lighter weight)
 */
export function useActiveImage() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  return {
    ...globalChatStateInstance.getActiveImage(),
  };
}

/**
 * Hook for accessing only profile image data (lighter weight)
 */
export function useProfileImage() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  return {
    ...globalChatStateInstance.getProfileImage(),
  };
}

/**
 * Hook for accessing only chat status (lighter weight)
 */
export function useChatStatus() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  return {
    chatStatus: globalChatStateInstance.getChatStatus(),
    setChatStatus: (status: ChatStatus) => globalChatStateInstance.setChatStatus(status),
  };
}

/**
 * Hook for accessing LiveKit room state and actions (specialized for video streaming)
 */
export function useGlobalLiveKitRoomState() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  // Memoized actions to prevent infinite loops
  const globalLKRoomStateActions = useMemo(() => ({
    setRoom: (room: Room | null) => globalChatStateInstance.setRoom(room),
    setPreJoinChoices: (preJoinChoices: LocalUserChoices | undefined) => globalChatStateInstance.setPreJoinChoices(preJoinChoices),
    setConnectionDetails: (connectionDetails: ConnectionDetails | undefined) => globalChatStateInstance.setConnectionDetails(connectionDetails),
    setIsInitiatingRoom: (isInitiatingRoom: boolean) => globalChatStateInstance.setIsInitiatingRoom(isInitiatingRoom),
  }), [globalChatStateInstance]);

  // Group room state data
  const globalLKRoomState = {
    room: globalChatStateInstance.getRoom(),
    preJoinChoices: globalChatStateInstance.getPreJoinChoices(),
    connectionDetails: globalChatStateInstance.getConnectionDetails(),
    isInitiatingRoom: globalChatStateInstance.getIsInitiatingRoom(),
  };

  // Return grouped state and actions
  return {
    globalLKRoomState,
    globalLKRoomStateActions,
  };
} 

/**
 * React hook for accessing inactivity timeout state
 */
export function useInactivityTimeoutState() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const triggerUpdate = () => forceUpdate({});
    const unsubscribe = globalChatStateInstance.subscribe(triggerUpdate);
    return unsubscribe;
  }, [globalChatStateInstance]);

  return {
    ...globalChatStateInstance.getInactivityTimeoutState(),
    setInactivityTimeoutState: (state: {
      isInactivityTimeoutPopupOpen: boolean;
      inactivityTimeoutReason: 'server_signal' | 'user_inactivity' | null;
    }) => globalChatStateInstance.setInactivityTimeoutState(state),
  };
}

/**
 * React hook for accessing centralized inactivity timer functions
 * This replaces the old useInactivityTimeout hook with centralized timer management
 *
 * USAGE:
 * - Call resetInactivityTimer() on user interactions (typing, clicking, sending messages)
 * - Timer starts/stops automatically with chat status changes
 * - No manual cleanup needed in components
 */
export function useInactivityTimer() {
  const { globalChatState: globalChatStateInstance } = useGlobalChatStateContext();

  return {
    resetInactivityTimer: () => globalChatStateInstance.resetInactivityTimer(),
    // clearInactivityTimer is internal only - handled automatically by globalChatState
  };
} 